/* eslint-disable camelcase */
import type { Store } from 'redux';
import { HttpClient } from '@piwikpro/http-crate';
import { Service, inject, ConfigService } from '@piwikpro/platform';
import { CustomNotification, Notifications, NotifyOnFail } from '@piwikpro/notification-crate';
import { Confirmation } from '@piwikpro/confirmation-crate';
import {
  getTransactionsFailed,
  getTransactionsInitialized,
  getTransactionsSucceeded,
  getCurrentSubscriptionFailed,
  getCurrentSubscriptionInitialized,
  getCurrentSubscriptionSucceeded,
  SubscriptionCurrentTierResponse,
  getTiersFailed,
  getTiersInitialized,
  getTiersSucceeded,
  TransactionsResponse,
  SubscriptionManagementUrlResponse,
  previewNewSubscriptionInitialized,
  previewNewSubscriptionSucceeded,
  previewNewSubscriptionFailed,
  applyNewSubscriptionInitialized,
  applyNewSubscriptionSucceeded,
  applyNewSubscriptionFailed,
  cancelCurrentSubscriptionInitialized,
  cancelCurrentSubscriptionSucceeded,
  cancelCurrentSubscriptionFailed,
  PreviewSubscriptionResponse,
  TierResponse,
  BillingCycle,
} from './redux';
import confirmationModalBody, { SubscriptionUpdateAction } from './components/confirmation/ApplyNewSubscriptionConfirmation';

type ApplyNewSubscriptionProps = {
  priceId: string
  action: SubscriptionUpdateAction
  immediateTransactionPrice: string
  recurringTransactionPrice: string
  nextBillingPeriod: string
  billingCycle?: BillingCycle
  creditsIssued: boolean
  onConfirmClick: () => void
}

@Service()
export class BillingService {
  constructor(
    @inject('store') private readonly store: Store,
    @inject('config') private config: ConfigService,
    @inject('ConfirmationCrate.confirmation') private readonly confirmation: Confirmation,
    @inject('NotificationCrate.interceptors.NotifyOnFail') private readonly notifyOnFailInterceptor: NotifyOnFail,
    @inject('NotificationCrate.interceptors.CustomNotification') private readonly customNotification: CustomNotification,
    @inject('NotificationCrate.notifications') private readonly notifications: Notifications,
    @inject('HttpCrate.httpClient') private httpClient: HttpClient,
  ) {}

  async getSubscriptionManagementUrl() {
    try {
      const response = await this.httpClient.request<SubscriptionManagementUrlResponse>({
        endpoint: `${this.config.get('SUBSCRIPTION_URL')}/tier/management-url`,
        method: 'GET',
      })
        .json()
        .send();


      return response;
    } catch (err) {
      this.notifications.push({
        id: 'payment-management-url-get-failed',
        text: 'administration:paddle.payment.current-subscription.manage-payment.redirect-error-notification',
        type: 'error',
        autoCloseSeconds: 5,
      });
      throw err;
    }
  }

  async getInvoiceUrl(transactionId: string) {
    try {
      const { url } = await this.httpClient.request<{ url: string }>({
        endpoint: `${this.config.get('SUBSCRIPTION_URL')}/invoices/${transactionId}`,
        method: 'GET',
      })
        .intercept(() => this.customNotification.withConfig({
          pending: {
            text: 'administration:paddle.billing.invoice-download.notification.pending',
            type: 'pending',
          },
          success: {
            text: 'administration:paddle.billing.invoice-download.notification.success',
            type: 'success',
          },
          error: {
            text: 'administration:paddle.billing.invoice-download.notification.error',
            type: 'error',
          },
        }))
        .json().send();

      return url;
    } catch (err) {
      throw err;
    }
  }

  async getTransactions() {
    this.store.dispatch(getTransactionsInitialized());

    try {
      const response = await this.httpClient.request({
        endpoint: `${this.config.get('SUBSCRIPTION_URL')}/transactions`,
        method: 'GET',
      }).json().send<TransactionsResponse>();

      this.store.dispatch(getTransactionsSucceeded(response));

      return response;
    } catch (err) {
      this.store.dispatch(getTransactionsFailed());

      throw err;
    }
  }

  async getCurrentSubscriptionTier() {
    this.store.dispatch(getCurrentSubscriptionInitialized());

    try {
      const response = await this.httpClient.request({
        endpoint: `${this.config.get('SUBSCRIPTION_URL')}/tier`,
        method: 'GET',
      }).json().send<SubscriptionCurrentTierResponse>();

      this.store.dispatch(getCurrentSubscriptionSucceeded(response));

      return response;
    } catch (err) {
      this.store.dispatch(getCurrentSubscriptionFailed());

      throw err;
    }
  }

  async getTiers() {
    this.store.dispatch(getTiersInitialized());

    try {
      const response = await this.httpClient.request({
        endpoint: `${this.config.get('SUBSCRIPTION_URL')}/tiers`,
        method: 'GET',
      }).json().send<TierResponse[]>();
      this.store.dispatch(getTiersSucceeded(response));

      return response;
    } catch (err) {
      this.store.dispatch(getTiersFailed());

      throw err;
    }
  }

  async previewNewSubscription(priceId: string) {
    this.store.dispatch(previewNewSubscriptionInitialized());

    try {
      const response = await this.httpClient.request({
        endpoint: `${this.config.get('SUBSCRIPTION_URL')}/tier/preview`,
        method: 'PATCH',
        body: {
          price_id: priceId,
        },
      })
        .intercept(this.notifyOnFailInterceptor)
        .json()
        .send<PreviewSubscriptionResponse>();

      this.store.dispatch(previewNewSubscriptionSucceeded(priceId));

      return response;
    } catch (err) {
      this.store.dispatch(previewNewSubscriptionFailed());

      throw err;
    }
  }


  async applyNewSubscription(
    {
      priceId,
      action,
      immediateTransactionPrice,
      recurringTransactionPrice,
      nextBillingPeriod,
      onConfirmClick,
      creditsIssued,
      billingCycle,
    }: ApplyNewSubscriptionProps,
  ) {
    this.store.dispatch(applyNewSubscriptionInitialized());

    let title = '';

    switch (action) {
      case 'downgrade':
        title = 'administration:marketing.change-plan-overlay.downgrade-confirmation.modal-title';
        break;
      case 'downgrade-billing-cycle-only':
        title = 'administration:marketing.change-plan-overlay.downgrade-billing-cycle-only-confirmation.modal-title';
        break;
      case 'upgrade-billing-cycle-only':
        title = 'administration:marketing.change-plan-overlay.upgrade-billing-cycle-only-confirmation.modal-title';
        break;
      case 'upgrade':
      default:
        title = 'administration:marketing.change-plan-overlay.upgrade-confirmation.modal-title';
        break;
    }

    return this.confirmation.confirm({
      title,
      type: 'customBody',
      applyBtnText: 'administration:marketing.change-plan-overlay.upgrade-confirmation.confirm-button',
      applyBtnAppearance: 'primary',
      // @ts-ignore:next-line
    }, () => confirmationModalBody({
      action, immediateTransactionPrice, recurringTransactionPrice, nextBillingPeriod, billingCycle, creditsIssued,
    })).then(async () => {
      onConfirmClick();
      await this.httpClient.request({
        endpoint: `${this.config.get('SUBSCRIPTION_URL')}/tier`,
        method: 'PATCH',
        body: {
          price_id: priceId,
        },
      })
        .intercept(this.notifyOnFailInterceptor)
        .json()
        .send<SubscriptionCurrentTierResponse>();

      this.store.dispatch(applyNewSubscriptionSucceeded(priceId));
    }).catch((err) => {
      this.store.dispatch(applyNewSubscriptionFailed());
      return Promise.reject(err);
    });
  }

  async cancelCurrentSubscription() {
    this.store.dispatch(cancelCurrentSubscriptionInitialized());

    try {
      const response = await this.httpClient.request({
        endpoint: `${this.config.get('SUBSCRIPTION_URL')}/tier`,
        method: 'DELETE',
      }).json().send<SubscriptionCurrentTierResponse>();

      this.store.dispatch(cancelCurrentSubscriptionSucceeded());

      return response;
    } catch (err) {
      this.store.dispatch(cancelCurrentSubscriptionFailed());

      throw err;
    }
  }
}
